import React from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogTitle,
} from '@material-ui/core';

import {
  CardWrapper,
  GraphQLErrorWrapper,
  Loader,
  SuccessSnackBar,
  CustomButton,
} from '../../generic';

import GroupForm from './GroupForm';

import {
  GROUP_QUERY,
  GROUP_UPDATE_MUTATION,
  GROUP_PROPAGATE_SETTINGS_MUTATION,
  GROUP_DELETE_MUTATION,
} from '../queries';

const GroupEditCard = (props) => {
  const {
    group,
  } = props;
  const navigate = useNavigate();

  const { t } = useTranslation('translations');
  const [successOpen, setSuccessOpen] = React.useState(false);
  const [askConfirmPropagateVisible, setAskConfirmPropagateVisible] = React.useState(false);
  const [askConfirmDeleteVisible, setAskConfirmDeleteVisible] = React.useState(false);

  const [
    groupUpdateMutationAction,
    {
      loading,
      error,
    },
  ] = useMutation(GROUP_UPDATE_MUTATION, {
    refetchQueries: () => {
      const result = [
        {
          query: GROUP_QUERY,
          variables: {
            id: group.id,
          },
        },
      ];
      return result;
    },
    awaitRefetchQueries: true,
  });

  const [
    groupPropagateSettingsMutationAction,
    {
      loading: loadingPropagate,
      error: errorPropagate,
    },
  ] = useMutation(GROUP_PROPAGATE_SETTINGS_MUTATION, {
    refetchQueries: () => {
      const result = [
        {
          query: GROUP_QUERY,
          variables: {
            id: group.id,
          },
        },
      ];
      return result;
    },
    awaitRefetchQueries: true,
  });

  const [
    groupDeleteMutationAction,
    {
      loading: loadingDelete,
      error: errorDelete,
    },
  ] = useMutation(GROUP_DELETE_MUTATION);

  const handleFormSubmit = async (data) => {
    try {
      const result = await groupUpdateMutationAction({
        variables: {
          input: {
            id: group.id,
            ...data,
          },
        },
      });
      if (result) setSuccessOpen(true);
    }
    catch (e) { } // eslint-disable-line
  };

  const handleDeleteGroup = async () => {
    try {
      const result = await groupDeleteMutationAction({
        variables: {
          input: {
            id: group.id,
          },
        },
      });
      setAskConfirmDeleteVisible(false);
      if (result) {
        setSuccessOpen(true);
        navigate('/groups', { replace: true });
      }
    }
    catch (e) {
      setAskConfirmDeleteVisible(false);
    }
  };

  const handlePropagate = async () => {
    try {
      const result = await groupPropagateSettingsMutationAction({
        variables: {
          input: {
            id: group.id,
          },
        },
      });
      if (result) setSuccessOpen(true);
      setAskConfirmPropagateVisible(false);
    }
    catch (e) { } // eslint-disable-line
  };

  return (
    <CardWrapper
      cardTitle={t('groups.edit.title')}
      cardBody={
        (
          <>
            <SuccessSnackBar open={successOpen} setOpen={setSuccessOpen} message="common.operationSuccess" />
            <GraphQLErrorWrapper error={error || errorPropagate || errorDelete} />
            <Loader loading={loading || loadingPropagate || loadingDelete} />
            <GroupForm
              group={group}
              onFormSubmit={handleFormSubmit}
            />
            <div style={{ width: '100%', marginTop: 20 }}>
              <Button
                fullWidth
                color="primary"
                variant="contained"
                onClick={() => setAskConfirmPropagateVisible(true)}
              >
                <Typography>{t('common.propagateSettings')}</Typography>
              </Button>
              <Dialog
                fullWidth
                maxWidth="sm"
                open={askConfirmPropagateVisible}
                onClose={() => setAskConfirmPropagateVisible(false)}
              >
                <DialogTitle id="draggable-dialog-title">
                  {t('common.confirmPropagateSettings')}
                </DialogTitle>
                <DialogActions>
                  <CustomButton autoFocus onClick={() => setAskConfirmPropagateVisible(false)} color="info">
                    {t('common.cancel')}
                  </CustomButton>
                  <CustomButton onClick={() => handlePropagate()} color="primary">
                    {t('common.ok')}
                  </CustomButton>
                </DialogActions>
              </Dialog>
            </div>
            <div style={{ width: '100%', marginTop: 20 }}>
              <Button
                fullWidth
                color="secondary"
                variant="contained"
                onClick={() => setAskConfirmDeleteVisible(true)}
              >
                <Typography>{t('common.delete')}</Typography>
              </Button>
              <Dialog
                fullWidth
                maxWidth="sm"
                open={askConfirmDeleteVisible}
                onClose={() => setAskConfirmDeleteVisible(false)}
              >
                <DialogTitle id="draggable-dialog-title">
                  {t('common.confirmDeleteGroup')}
                </DialogTitle>
                <DialogActions>
                  <CustomButton autoFocus onClick={() => setAskConfirmDeleteVisible(false)} color="info">
                    {t('common.cancel')}
                  </CustomButton>
                  <CustomButton onClick={() => handleDeleteGroup()} color="primary">
                    {t('common.ok')}
                  </CustomButton>
                </DialogActions>
              </Dialog>
            </div>
          </>
        )
      }
    />
  );
};

const propTypes = {
  group: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
};

GroupEditCard.propTypes = propTypes;

export default GroupEditCard;
